import { useState } from 'react';
import { Search, GraduationCap, Plus, X } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatCard } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post } from '@/lib/api';

interface Course {
  id: string;
  title: string;
  category: string;
  format: string;
  duration_minutes: number;
  mandatory: boolean;
  enrollment_count: number;
  completion_rate: number;
}

export function TrainingPage() {
  const [search, setSearch] = useState('');
  const { data, loading, refetch } = useApi<{ courses: Course[] }>(`training/courses?search=${search}`, [search]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [courseForm, setCourseForm] = useState({
    title: '',
    category: 'technical',
    format: 'online',
    duration_minutes: '60',
    mandatory: false,
    description: '',
    url: '',
  });

  async function handleCreateCourse() {
    if (!courseForm.title.trim()) {
      setCreateError('Course title is required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await post('training/courses', {
        title: courseForm.title.trim(),
        category: courseForm.category,
        format: courseForm.format,
        duration_minutes: parseInt(courseForm.duration_minutes) || 60,
        mandatory: courseForm.mandatory,
        description: courseForm.description || undefined,
        url: courseForm.url || undefined,
      });
      setShowCreate(false);
      setCourseForm({ title: '', category: 'technical', format: 'online', duration_minutes: '60', mandatory: false, description: '', url: '' });
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create course');
    } finally {
      setCreating(false);
    }
  }

  const courses = data?.courses ?? [];
  const mandatory = courses.filter(c => c.mandatory);
  const inputClass = 'w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total Courses" value={courses.length} icon={<GraduationCap className="w-5 h-5" />} accent />
        <StatCard label="Mandatory" value={mandatory.length} />
        <StatCard label="Avg Completion" value={courses.length > 0 ? `${Math.round(courses.reduce((a, c) => a + c.completion_rate, 0) / courses.length)}%` : '0%'} />
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input type="text" placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-2 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-amber-500" />
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
          <Plus className="w-4 h-4" /> Create Course
        </button>
      </div>

      {loading ? <div className="text-muted-foreground">Loading...</div> : (
        <DataTable
          columns={[
            { key: 'title', header: 'Course', render: (r: Course) => <span className="font-medium">{r.title}</span> },
            { key: 'category', header: 'Category', render: (r: Course) => <span className="capitalize">{r.category}</span> },
            { key: 'format', header: 'Format', render: (r: Course) => <span className="capitalize">{r.format.replace('_', ' ')}</span> },
            { key: 'duration_minutes', header: 'Duration', render: (r: Course) => `${r.duration_minutes}min` },
            { key: 'mandatory', header: 'Required', render: (r: Course) => r.mandatory ? <span className="text-amber-400 text-xs">Required</span> : <span className="text-muted-foreground text-xs">Optional</span> },
            { key: 'enrollment_count', header: 'Enrolled' },
            { key: 'completion_rate', header: 'Completion', render: (r: Course) => `${r.completion_rate}%` },
          ]}
          data={courses}
        />
      )}

      {/* Create Course Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Create Training Course</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Course Title *</label>
                <input value={courseForm.title} onChange={e => setCourseForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Azure Fundamentals" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Category</label>
                  <select value={courseForm.category} onChange={e => setCourseForm(f => ({ ...f, category: e.target.value }))} className={inputClass}>
                    <option value="technical">Technical</option>
                    <option value="compliance">Compliance</option>
                    <option value="security">Security</option>
                    <option value="leadership">Leadership</option>
                    <option value="soft_skills">Soft Skills</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="certification">Certification</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Format</label>
                  <select value={courseForm.format} onChange={e => setCourseForm(f => ({ ...f, format: e.target.value }))} className={inputClass}>
                    <option value="online">Online</option>
                    <option value="in_person">In-Person</option>
                    <option value="video">Video</option>
                    <option value="self_paced">Self-Paced</option>
                    <option value="live_virtual">Live Virtual</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Duration (minutes)</label>
                  <input type="number" value={courseForm.duration_minutes} onChange={e => setCourseForm(f => ({ ...f, duration_minutes: e.target.value }))} className={inputClass} />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <input type="checkbox" id="mandatory" checked={courseForm.mandatory} onChange={e => setCourseForm(f => ({ ...f, mandatory: e.target.checked }))} className="rounded border-border" />
                  <label htmlFor="mandatory" className="text-sm text-foreground">Mandatory</label>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Description</label>
                <textarea value={courseForm.description} onChange={e => setCourseForm(f => ({ ...f, description: e.target.value }))} placeholder="Course description..." rows={2} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Course URL</label>
                <input value={courseForm.url} onChange={e => setCourseForm(f => ({ ...f, url: e.target.value }))} placeholder="https://learn.microsoft.com/..." className={inputClass} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void handleCreateCourse()} disabled={creating} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Course'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
